import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { ChatBubbleLeftIcon, CheckCircleIcon, StarIcon, UserGroupIcon, UserIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import {
  obtenerTemplateWhatsApp,
  actualizarTemplateWhatsApp,
  obtenerHorariosWhatsApp,
  actualizarHorariosWhatsApp,
  obtenerTemplateReview,
  actualizarTemplateReview
} from '../api/backend_v2';
import { sanitizarMensajeWhatsApp, abrirWhatsApp } from '../utils/whatsappHelper';

const Mensajeria = () => {
  const [tab, setTab] = useState('anteojos');
  
  // Estado para mensaje de anteojos
  const [template, setTemplate] = useState('');
  const [defaultTemplate, setDefaultTemplate] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [horarioSemana, setHorarioSemana] = useState('');
  const [horarioSabado, setHorarioSabado] = useState('');
  
  // Estado para mensaje de review
  const [templateReview, setTemplateReview] = useState('');
  const [defaultTemplateReview, setDefaultTemplateReview] = useState('');
  const [placeholdersReview, setPlaceholdersReview] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingHorarios, setSavingHorarios] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [googleReviewLink] = useState('https://g.page/r/CUueNigCrCoTEBE/review');
  const [modoPreview, setModoPreview] = useState('tutor'); // 'directo' | 'tutor'

  const sampleValuesReview = useMemo(() => {
    const horaActual = new Date().getHours();
    const saludo = horaActual < 13 
      ? 'Hola, Pedro, ¡buenos días!'
      : 'Hola, Pedro, ¡buenas tardes!';
    
    return {
      '{destinatario_nombre}': saludo,
      '{cliente_nombre}': 'Pedro',
      '{google_review_link}': googleReviewLink
    };
  }, [googleReviewLink]);

  useEffect(() => {
    const cargarTemplates = async () => {
      setLoading(true);
      try {
        const [templateData, horariosData, reviewData] = await Promise.all([
          obtenerTemplateWhatsApp(),
          obtenerHorariosWhatsApp(),
          obtenerTemplateReview()
        ]);
        
        setTemplate(templateData.template || '');
        setDefaultTemplate(templateData.default_template || '');
        setPlaceholders(templateData.placeholders || []);
        setHorarioSemana(horariosData.horario_semana || '');
        setHorarioSabado(horariosData.horario_sabado || '');
        
        setTemplateReview(reviewData.template || '');
        setDefaultTemplateReview(reviewData.default_template || '');
        setPlaceholdersReview(reviewData.placeholders || []);
      } catch (e) {
        Swal.fire('Error', e.message, 'error');
      } finally {
        setLoading(false);
      }
    };

    cargarTemplates();
  }, []);

  const handleGuardar = async () => {
    const textoSanitizado = sanitizarMensajeWhatsApp(template.trim());
    if (!textoSanitizado) {
      Swal.fire('Error', 'El mensaje no puede estar vacío', 'error');
      return;
    }
    setSaving(true);
    try {
      await actualizarTemplateWhatsApp(textoSanitizado);
      setTemplate(textoSanitizado);
      Swal.fire('Listo', 'Mensaje guardado correctamente', 'success');
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRestaurar = async () => {
    if (!defaultTemplate) {
      return;
    }
    const result = await Swal.fire({
      title: '¿Restaurar mensaje predeterminado?',
      text: 'Se reemplazará el mensaje actual.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, restaurar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      setTemplate(defaultTemplate);
      Swal.fire('Restaurado', 'Template predeterminado cargado', 'success');
    }
  };

  const handleGuardarHorarios = async () => {
    if (!horarioSemana.trim() || !horarioSabado.trim()) {
      Swal.fire('Error', 'Los horarios no pueden estar vacíos', 'error');
      return;
    }
    setSavingHorarios(true);
    try {
      await actualizarHorariosWhatsApp(horarioSemana.trim(), horarioSabado.trim());
      Swal.fire('Listo', 'Horarios actualizados correctamente', 'success');
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setSavingHorarios(false);
    }
  };

  const handleGuardarReview = async () => {
    const textoSanitizado = sanitizarMensajeWhatsApp(templateReview.trim());
    if (!textoSanitizado) {
      Swal.fire('Error', 'El mensaje no puede estar vacío', 'error');
      return;
    }
    setSavingReview(true);
    try {
      await actualizarTemplateReview(textoSanitizado);
      setTemplateReview(textoSanitizado);
      Swal.fire('Listo', 'Mensaje de review guardado correctamente', 'success');
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setSavingReview(false);
    }
  };

  const handleRestaurarReview = async () => {
    if (!defaultTemplateReview) {
      return;
    }
    const result = await Swal.fire({
      title: '¿Restaurar mensaje predeterminado?',
      text: 'Se reemplazará el mensaje actual.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, restaurar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      setTemplateReview(defaultTemplateReview);
      Swal.fire('Restaurado', 'Template predeterminado cargado', 'success');
    }
  };

  const preview = useMemo(() => {
    let rendered = template || '';
    const esConTutor = modoPreview === 'tutor';
    const destPila = esConTutor ? 'Susana' : 'Gisela';
    const cliPila = 'Gisela';
    const horaActual = new Date().getHours();
    const saludo = horaActual < 13 ? `¡Hola ${destPila}, buenos días!` : `¡Hola ${destPila}, buenas tardes!`;
    const mensajeAnteojo = esConTutor
      ? `Los anteojos de ${cliPila} ya están listos para retirar.`
      : 'Tus anteojos ya están listos para retirar.';

    rendered = rendered.replace(/{saludo}/g, saludo);
    rendered = rendered.replace(/¡?Hola\s+\{destinatario_nombre\}!?/gi, `¡Hola ${destPila}!`);
    rendered = rendered.replace(/{destinatario_nombre}/g, saludo);
    rendered = rendered.replace(/{mensaje_anteojo}/g, mensajeAnteojo);

    if (esConTutor) {
      rendered = rendered.replace(
        /(Tus anteojos (?:ya )?están listos para retirar!?|Tu anteojo está listo para retirar\.?)/gi,
        `Los anteojos de ${cliPila} ya están listos para retirar!`
      );
    }

    rendered = rendered.replace(/{cliente_nombre}/g, cliPila);
    rendered = rendered.replace(/{tutor_nombre}/g, esConTutor ? 'Susana' : '');
    rendered = rendered.replace(/{nro_sobre}/g, '3088');
    rendered = rendered.replace(/{horario_semana}/g, horarioSemana || 'Lunes a Viernes 10:30 - 19:00 hs');
    rendered = rendered.replace(/{horario_sabado}/g, horarioSabado || 'Sábados 10:30 - 14:00 hs');

    return rendered.trim();
  }, [template, modoPreview, horarioSemana, horarioSabado]);

  const previewReview = useMemo(() => {
    let rendered = templateReview || '';
    const esConTutor = modoPreview === 'tutor';
    const destPila = esConTutor ? 'Susana' : 'Gisela';
    const cliPila = 'Gisela';

    rendered = rendered.replace(/{destinatario_nombre}/g, destPila);
    rendered = rendered.replace(/{cliente_nombre}/g, cliPila);
    rendered = rendered.replace(/{tutor_nombre}/g, esConTutor ? 'Susana' : '');
    rendered = rendered.replace(/{google_review_link}/g, googleReviewLink);
    return rendered.trim();
  }, [templateReview, modoPreview, googleReviewLink]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white shadow rounded-lg p-6">
            <p className="text-gray-600">Cargando configuración de mensajería...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <ChatBubbleLeftIcon className="h-8 w-8 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900">Mensajería</h2>
        </div>
        <p className="text-gray-600 mb-6">
          Personaliza los mensajes de WhatsApp que se envían a tus clientes.
        </p>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-gray-200">
          <button
            onClick={() => setTab('anteojos')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors flex items-center gap-2 ${
              tab === 'anteojos'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <CheckCircleIcon className="h-5 w-5" />
            Anteojos listos
          </button>
          <button
            onClick={() => setTab('review')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors flex items-center gap-2 ${
              tab === 'review'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <StarIcon className="h-5 w-5" />
            Mensaje de review
          </button>
        </div>

        {/* TAB: Anteojos Listos */}
        {tab === 'anteojos' && (
          <>
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Mensaje actual</h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleRestaurar}
                    className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Restaurar predeterminado
                  </button>
                  <button
                    type="button"
                    onClick={handleGuardar}
                    disabled={saving}
                    className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {saving ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                </div>
              </div>

              <textarea
                rows={10}
                className="w-full border border-gray-300 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                placeholder="Escribe el mensaje que verá el cliente"
              />

              <div className="mt-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">Variables disponibles</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {placeholders.map((item) => (
                    <div key={item.key} className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                      <span className="font-mono text-gray-800">{item.key}</span>
                      <span className="ml-2">{item.descripcion}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Horarios del negocio</h3>
                <button
                  type="button"
                  onClick={handleGuardarHorarios}
                  disabled={savingHorarios}
                  className="px-4 py-2 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {savingHorarios ? 'Guardando...' : 'Guardar horarios'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lunes a Viernes
                  </label>
                  <input
                    type="text"
                    value={horarioSemana}
                    onChange={(e) => setHorarioSemana(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Ej: 10:30 - 19:00 hs"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sábados
                  </label>
                  <input
                    type="text"
                    value={horarioSabado}
                    onChange={(e) => setHorarioSabado(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Ej: 10:30 - 14:00 hs"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Estos horarios se insertan automáticamente en el mensaje de WhatsApp.
              </p>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-gray-800">Vista previa</h3>
                  <div className="inline-flex rounded-lg bg-gray-100 p-0.5 text-xs font-medium">
                    <button
                      type="button"
                      onClick={() => setModoPreview('tutor')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors ${
                        modoPreview === 'tutor'
                          ? 'bg-white text-blue-700 shadow-xs font-semibold'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <UserGroupIcon className="w-4 h-4 text-indigo-600 shrink-0" />
                      <span>Con tutor/a</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setModoPreview('directo')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors ${
                        modoPreview === 'directo'
                          ? 'bg-white text-blue-700 shadow-xs font-semibold'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <UserIcon className="w-4 h-4 text-blue-600 shrink-0" />
                      <span>Cliente directo</span>
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!preview) return;
                    abrirWhatsApp('', preview);
                  }}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5 transition-colors shadow-xs cursor-pointer"
                >
                  <ChatBubbleBottomCenterTextIcon className="w-4 h-4 shrink-0 text-white" />
                  <span>Probar en WhatsApp</span>
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                {modoPreview === 'tutor'
                  ? 'Simulación: Destinatario es la tutora (Susana), notificando que el anteojo de su representada (Gisela) está listo.'
                  : 'Simulación: Destinatario es la cliente directa (Gisela), notificando que su anteojo está listo.'}
              </p>
              <div className="whitespace-pre-wrap border border-dashed border-gray-300 rounded-md p-4 text-sm text-gray-800 bg-gray-50">
                {preview || 'Sin contenido para previsualizar.'}
              </div>
            </div>
          </>
        )}

        {/* TAB: Mensaje de Review */}
        {tab === 'review' && (
          <>
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Mensaje de review</h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleRestaurarReview}
                    className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Restaurar predeterminado
                  </button>
                  <button
                    type="button"
                    onClick={handleGuardarReview}
                    disabled={savingReview}
                    className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {savingReview ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Este mensaje se enviará cuando se marque un anteojo como "Entregado". Incluye la variable {'{google_review_link}'} para invitar a clientes a dejar una reseña.
              </p>

              <textarea
                rows={10}
                className="w-full border border-gray-300 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={templateReview}
                onChange={(e) => setTemplateReview(e.target.value)}
                placeholder="Escribe el mensaje para solicitar review"
              />

              <div className="mt-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">Variables disponibles</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                    <span className="font-mono text-gray-800">{'{destinatario_nombre}'}</span>
                    <span className="ml-2">Nombre de quien recibe el mensaje</span>
                  </div>
                  <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                    <span className="font-mono text-gray-800">{'{cliente_nombre}'}</span>
                    <span className="ml-2">Nombre del cliente</span>
                  </div>
                  <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                    <span className="font-mono text-gray-800">{'{google_review_link}'}</span>
                    <span className="ml-2">Link para dejar review en Google</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-gray-800">Vista previa</h3>
                  <div className="inline-flex rounded-lg bg-gray-100 p-0.5 text-xs font-medium">
                    <button
                      type="button"
                      onClick={() => setModoPreview('tutor')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors ${
                        modoPreview === 'tutor'
                          ? 'bg-white text-blue-700 shadow-xs font-semibold'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <UserGroupIcon className="w-4 h-4 text-indigo-600 shrink-0" />
                      <span>Con tutor/a</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setModoPreview('directo')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors ${
                        modoPreview === 'directo'
                          ? 'bg-white text-blue-700 shadow-xs font-semibold'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <UserIcon className="w-4 h-4 text-blue-600 shrink-0" />
                      <span>Cliente directo</span>
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!previewReview) return;
                    abrirWhatsApp('', previewReview);
                  }}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5 transition-colors shadow-xs cursor-pointer"
                >
                  <ChatBubbleBottomCenterTextIcon className="w-4 h-4 shrink-0 text-white" />
                  <span>Probar en WhatsApp</span>
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                {modoPreview === 'tutor'
                  ? 'Simulación: Destinatario es la tutora (Susana).'
                  : 'Simulación: Destinatario es la cliente directa (Gisela).'}
              </p>
              <div className="whitespace-pre-wrap border border-dashed border-gray-300 rounded-md p-4 text-sm text-gray-800 bg-gray-50">
                {previewReview || 'Sin contenido para previsualizar.'}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Mensajeria;
